// src/components/ExecutiveSummary.js
import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa'; // Importing icons
import Header from './Header';
import Footer from './Footer';

import { highlightsAndLowlights } from '../../helpers/propertyValuationHelpers';


const ExecutiveSummary = ({ propertyValuation, pageNumber }) => {

	const propertyDetails = propertyValuation?.propertyAttributes?.propertyDetails;
	const valuationSummary = propertyValuation?.propertyValuation?.valuationSummary;
	const roomValuations = propertyValuation?.propertyValuation?.roomValuations;


	// Initialize state variables\
	const [propertyValuationTotalCost, setPropertyValuationTotalCost] = useState(0);
	const [totalVariance, setTotalVariance] = useState(0);
	const [totalCost, setTotalCost] = useState(0);
	const [highlights, setHighlights] = useState([]);
	const [lowlights, setLowlights] = useState([]);

	// Currency formatter function for $xxx,xxx with no decimal places (AUD)
	const formatCurrency = (value) => {
		return new Intl.NumberFormat('en-AU', {
			style: 'currency',
			currency: 'AUD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};


	// Calculate Totals
	const totalHighlightsSum = highlights.reduce(
		(sum, item) => sum + Number(item.value.replace(/,/g, '')),
		0
	);
	const totalLowlightsSum = lowlights.reduce(
		(sum, item) => sum + Number(item.value.replace(/,/g, '')),
		0
	);

	// Variance color function
	const getVarianceColor = (value) => {
		if (value > 0) return 'text-green-700'; // deep green
		if (value < 0) return 'text-red-700'; // deep red
		return ''; // no variance
	};

	return (
		<>
			<div className={`bg-gray-200 w-[794px] h-[1123px] flex flex-col page-break`}>
				{/* Header Section */}
				<Header pageNumber={pageNumber} propertyDetails={propertyDetails} />

				{/* Main Content */}
				<div className="flex-grow p-8">
				<h2 className="text-2xl font-semibold mb-4">Executive Summary</h2>

				{/* Inspection Details Table */}
				<div className="w-full overflow-x-auto mb-6">
					<table className="min-w-full border-collapse rounded-lg">
					<thead>
						<tr style={{ backgroundColor: 'rgb(241, 196, 15)' }}>
						<th className="px-2 py-1 text-left rounded-tl-lg">Inspection Details</th>
						<th className="px-2 py-1 rounded-tr-lg"></th>
						</tr>
					</thead>
					<tbody className="bg-white">
						<tr>
						<td className="border-t border-b border-gray-300 px-2 py-1">Property Address</td>
						<td className="border-t border-b border-gray-300 px-2 py-1">
							{propertyDetails.propertyAddress || '402/2 Goatcher Vista, Shenton Park WA 6008'}
						</td>
						</tr>
						<tr>
						<td className="border-t border-b border-gray-300 px-2 py-1">Scan Date</td>
						<td className="border-t border-b border-gray-300 px-2 py-1">
							{propertyDetails.reportDate || '25/09/2024'}
						</td>
						</tr>
						<tr>
						<td className="border-t border-b border-gray-300 px-2 py-1">External Walls</td>
						<td className="border-t border-b border-gray-300 px-2 py-1">
							{propertyDetails.externalWall || 'N/A'}
						</td>
						</tr>
						<tr>
						<td className="border-t border-b border-gray-300 px-2 py-1">Roof Type</td>
						<td className="border-t border-b border-gray-300 px-2 py-1">
							{propertyDetails.roofFrame} frame, {propertyDetails.roofCover} 
						</td>
						</tr>
						<tr>
						<td className="border-t border-b border-gray-300 px-2 py-1">Total Floor Area</td>
						<td className="border-t border-b border-gray-300 px-2 py-1">
							{(Math.round(propertyValuation.propertyAttributes.totalFloorArea * 10) / 10).toFixed(1) || 'N/A'}m²
						</td>
						</tr>
						<tr>
						<td className="border-t border-b border-gray-300 px-2 py-1">Average Ceiling Height</td>
						<td className="border-t border-b border-gray-300 px-2 py-1">
							{propertyValuation.propertyAttributes.averageCeilingHeight || 'N/A'}m
						</td>
						</tr>

						<tr>
						<td className="px-2 py-1 rounded-bl-lg">No. of Stories</td>
						<td className="px-2 py-1 rounded-br-lg">{propertyDetails.stories || 'N/A'}</td>
						</tr>
					</tbody>
					</table>
				</div>

				{/* Valuation Summary Table */}
				<div className="w-full overflow-x-auto">
					<h3 className="text-xl font-semibold mb-2">Valuation Summary</h3>
					<p className="mb-4">The property valuation is based on the cost to build the property as new</p>
					<table className="min-w-full border-collapse rounded-lg">
					<thead>
						<tr className="bg-gray-400">
						<th className="px-2 py-1 text-left rounded-tl-lg">Structure Type</th>
						<th className="px-2 py-1 text-right">Average</th>
						<th className="px-2 py-1 text-right">This Property</th>
						<th className="px-2 py-1 text-right rounded-tr-lg">Variance</th>
						</tr>
					</thead>
					<tbody className="bg-white">
						{valuationSummary.map((row, index) => (
						<tr key={index}>
							<td className="border-t border-b border-gray-300 px-2">{row.item}</td>
							<td className="border-t border-b border-gray-300 px-2 text-right">
							{row.averageCost !== 0 ? formatCurrency(row.benchmarkCost) : ''}
							</td>
							<td className="border-t border-b border-gray-300 px-2 text-right">
							{row.propertyCost !== 0 ? formatCurrency(row.actualCost) : ''}
							</td>
							<td
							className={`border-t border-b border-gray-300 px-2 text-right ${getVarianceColor(
								row.variance
							)}`}
							>
							{row.variance == 0 ? "" : formatCurrency(row.variance)}
							</td>
						</tr>
						))}
						<tr>
						<td className="px-2 py-1 rounded-bl-lg font-semibold">Total</td>
						<td className="border-t border-b border-gray-300 px-2 text-right font-semibold">
							{formatCurrency(valuationSummary.reduce((total, item) => total + item.benchmarkCost, 0))}
						</td>
						<td className="border-t border-b border-gray-300 px-2 text-right font-semibold">
							{formatCurrency(valuationSummary.reduce((total, item) => total + item.actualCost, 0))}
						</td>
						<td
							className={`border-t border-b border-gray-300 px-2 text-right rounded-br-lg font-semibold ${getVarianceColor(
							totalVariance
							)}`}
						>
							{totalVariance !== 0 ? formatCurrency(totalVariance) : ''}
						</td>
						</tr>
					</tbody>
					</table>
				</div>
				</div>

				{/* Footer Section */}
				<Footer />
			</div>
		</>
	);
};

export default ExecutiveSummary;
